import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface PortPro {
  id: string;
  port_id: string;
  nom_entreprise: string;
  specialite: string;
  description: string | null;
  adresse: string | null;
  code_postal: string | null;
  ville: string | null;
  telephone: string | null;
  email: string | null;
  site_web: string | null;
  logo_url: string | null;
  horaires: string | null;
  actif: boolean;
  ordre_affichage: number;
  created_at: string;
  updated_at: string;
}

export const SPECIALITES = [
  'Ship',
  'Mécanicien',
  'Voilier',
  'Électronique',
  'École de voile',
  'Bateau école',
  'Maintenance voile',
  'Location',
  'Carénage',
  'Accastillage',
  'Plongée',
  'Convoyage',
  'Expert maritime',
  'Autre',
] as const;

export const usePortById = (id?: string) => {
  return useQuery({
    queryKey: ['port', id],
    enabled: !!id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('ports' as any)
        .select('*')
        .eq('id', id!)
        .maybeSingle();
      if (error) throw error;
      return data as any;
    },
  });
};

export const usePortProfessionnels = (portId?: string) => {
  return useQuery({
    queryKey: ['port-pros', portId],
    enabled: !!portId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('port_professionnels' as any)
        .select('*')
        .eq('port_id', portId!)
        .eq('actif', true)
        .order('ordre_affichage', { ascending: false })
        .order('nom_entreprise', { ascending: true });
      if (error) throw error;
      return (data || []) as unknown as PortPro[];
    },
  });
};

export const useAdminPortPros = () => {
  const qc = useQueryClient();

  const list = useQuery({
    queryKey: ['admin-port-pros'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('port_professionnels' as any)
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data || []) as unknown as PortPro[];
    },
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['admin-port-pros'] });
    qc.invalidateQueries({ queryKey: ['port-pros'] });
  };

  const create = useMutation({
    mutationFn: async (payload: Partial<PortPro>) => {
      const { error } = await supabase.from('port_professionnels' as any).insert(payload as any);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('Professionnel ajouté');
      invalidate();
    },
    onError: (e: any) => toast.error(e.message || 'Erreur'),
  });

  const update = useMutation({
    mutationFn: async ({ id, ...payload }: Partial<PortPro> & { id: string }) => {
      const { error } = await supabase
        .from('port_professionnels' as any)
        .update(payload as any)
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('Professionnel mis à jour');
      invalidate();
    },
    onError: (e: any) => toast.error(e.message || 'Erreur'),
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('port_professionnels' as any).delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('Professionnel supprimé');
      invalidate();
    },
    onError: (e: any) => toast.error(e.message || 'Erreur'),
  });

  return { ...list, create, update, remove };
};

export const uploadPortProLogo = async (file: File): Promise<string> => {
  const ext = file.name.split('.').pop();
  const path = `${crypto.randomUUID()}.${ext}`;
  const { error } = await supabase.storage.from('port-pros-logos').upload(path, file, {
    cacheControl: '3600',
    upsert: false,
  });
  if (error) throw error;
  const { data } = supabase.storage.from('port-pros-logos').getPublicUrl(path);
  return data.publicUrl;
};
